import { useState, useEffect, useRef } from 'react';
import {
  Card,
  Button,
  Input,
  Select,
  Tag,
  Space,
  Alert,
  Typography,
  Divider,
  Spin,
} from 'antd';
import { SaveOutlined, ExperimentOutlined } from '@ant-design/icons';
import type { FieldVerification } from '@ute/mapper-ui-components';
import type { DocTypeDataField } from '@ute/mapper-ui-components';
import { mappingApi } from '../../services/api/mapping';

const { Text } = Typography;

interface Props {
  mappingKey: string;
  pdfHash: string;
  fieldKey: string;
  field: FieldVerification;
  docTypeField?: DocTypeDataField;
  tenantId?: string;
  /** Current template data for this mapping, used to read existing coord/pattern. */
  templateData?: Record<string, any>;
  /** Unsaved Advanced panel edits (dateFormat, locale, etc.) — merged into every candidate test. */
  liveAdvancedValues?: Record<string, string | undefined>;
  /** Called after a successful save so parent can re-fetch template data. */
  onSaved?: () => void;
  /** Enables a draggable box on the PDF for this field, seeded from its resolved box.
   * Called automatically when "Extract by" is set to Coordinates. */
  onActivateBox?: (
    fieldKey: string,
    seedBox?: { x: number; y: number; w: number; h: number } | null
  ) => void;
  /** Hides this field's draggable box (called when "Extract by" leaves Coordinates). */
  onDeactivateBox?: (fieldKey: string) => void;
  /** Moves this field's box to a different page (from the Page No dropdown), 0-based. */
  onSetBoxPage?: (fieldKey: string, pageNo: number) => void;
  /** Discards uncommitted box edits for this field (drag, page override) and exits edit mode.
   * Called automatically when the panel closes (Cancel/Apply/OK) so edits revert cleanly. */
  onDiscardBox?: (fieldKey: string) => void;
  /** Raw PDF text, fetched once at verification start and shared across fields.
   * `undefined` = still loading; `string` (incl. empty) = loaded. */
  pdfText?: string;
  /** Resolves the field's current box via the wizard's highlight logic (coords + pattern). */
  resolveBox?: () => { x: number; y: number; w: number; h: number } | null;
  /** Returns the current dragged box as a pdfmapper coords string, or null if untouched. */
  getBoxEdit?: (fieldKey: string) => string | null;
}

type EditMode = 'pattern' | 'coords';

interface MatchResult {
  count: number;
  /** All distinct captured values (group 1 if present, else full match). */
  values: string[];
  error: string | null;
}

/** Run regex client-side against PDF text. Handles Java-style (?i) prefix. */
function evalPattern(pattern: string, text: string): MatchResult {
  if (!pattern.trim()) return { count: 0, values: [], error: null };
  try {
    let flags = 'gm';
    let cleanPattern = pattern;
    if (pattern.startsWith('(?i)')) {
      flags = 'gmi';
      cleanPattern = pattern.slice(4);
    } else if (pattern.startsWith('(?mi)') || pattern.startsWith('(?im)')) {
      flags = 'gmi';
      cleanPattern = pattern.slice(5);
    }
    const re = new RegExp(cleanPattern, flags);
    const matches = Array.from(text.matchAll(re));
    // Use capture group 1 when defined, else the full match
    const values = matches.map((m) => (m[1] ?? m[0]).trim()).filter(Boolean);
    return { count: matches.length, values, error: null };
  } catch (e: any) {
    return { count: 0, values: [], error: e.message };
  }
}

export function FieldToolsPanel({
  mappingKey,
  pdfHash,
  fieldKey,
  field,
  docTypeField,
  tenantId,
  templateData,
  liveAdvancedValues,
  onSaved,
  onActivateBox,
  onDeactivateBox,
  onSetBoxPage,
  onDiscardBox,
  getBoxEdit,
  resolveBox,
  pdfText,
}: Props) {
  const fieldType = docTypeField?.dataType?.name ?? 'STRING';

  // Determine current extraction source from template data
  const hasPattern = !!templateData?.[`${fieldKey}Pattern`];
  const hasCoords  = !!templateData?.[`${fieldKey}Coords`];
  const currentPageNo = templateData?.[`${fieldKey}PageNo`] ?? '0';

  const [mode, setMode] = useState<EditMode>(hasPattern ? 'pattern' : 'coords');
  const [patternValue, setPatternValue] = useState(templateData?.[`${fieldKey}Pattern`] ?? '');
  const [pageNo, setPageNo] = useState<string>(currentPageNo);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<{ value: string | null; matched: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // PDF text for client-side match-count is provided by the parent (fetched once at
  // verification start). `undefined` = still loading.
  const textLoading = pdfText === undefined;
  const [matchResult, setMatchResult] = useState<MatchResult | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce regex evaluation against PDF text whenever patternValue changes
  useEffect(() => {
    if (mode !== 'pattern') return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (pdfText !== undefined) {
        setMatchResult(evalPattern(patternValue, pdfText));
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [patternValue, pdfText, mode]);

  // Re-evaluate when text loads
  useEffect(() => {
    if (pdfText !== undefined && mode === 'pattern') {
      setMatchResult(evalPattern(patternValue, pdfText));
    }
  }, [pdfText]);

  // Resolve the field's box during render (one field panel is open at a time, so cheap).
  // Only in coords mode — pattern mode hides the box.
  const resolved = mode === 'coords' ? resolveBox?.() ?? null : null;
  const resolvedKey = resolved
    ? `${resolved.x},${resolved.y},${resolved.w},${resolved.h}`
    : '';

  // Refs so the box-driving effect doesn't re-fire on callback/box identity churn.
  const resolvedRef = useRef(resolved);
  resolvedRef.current = resolved;
  const activateRef = useRef(onActivateBox);
  activateRef.current = onActivateBox;
  const deactivateRef = useRef(onDeactivateBox);
  deactivateRef.current = onDeactivateBox;
  const discardRef = useRef(onDiscardBox);
  discardRef.current = onDiscardBox;

  // Drive the PDF overlay from the "Extract by" toggle:
  //   Coordinates → show a draggable box seeded from the field's current value/selection
  //                 (re-seeds when that value moves, unless the user dragged it)
  //   Pattern     → hide the box
  useEffect(() => {
    if (mode === 'coords') {
      activateRef.current?.(fieldKey, resolvedRef.current);
    } else {
      deactivateRef.current?.(fieldKey);
    }
  }, [mode, fieldKey, resolvedKey]);

  // When the panel closes (Cancel/Apply/OK flips the field off "Wrong", or the user
  // switches fields), discard any uncommitted box edits and exit PDF edit mode.
  useEffect(() => {
    return () => {
      discardRef.current?.(fieldKey);
    };
  }, [fieldKey]);

  // Source badge
  const sourceLabel = hasPattern
    ? 'Pattern'
    : hasCoords
    ? 'Template coord'
    : 'Textract fallback';
  const sourceColor = hasPattern ? 'purple' : hasCoords ? 'blue' : 'default';

  const handleTest = async () => {
    setTesting(true);
    setError(null);
    setTestResult(null);
    try {
      const candidateData = {
        ...(templateData ?? {}),
        ...(liveAdvancedValues ?? {}),
        ...buildPatch(),
      };
      const res = await mappingApi.testTemplateCandidate(mappingKey, candidateData, tenantId);
      const extraction = res.data?.data?.[0];
      const extracted = extraction?.[fieldKey];
      setTestResult({
        value: extracted ?? null,
        matched: extracted != null && extracted !== '',
      });
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  const buildPatch = (): Record<string, any> => {
    const patch: Record<string, any> = {};
    if (mode === 'pattern') {
      patch[`${fieldKey}Pattern`] = patternValue;
      patch[`${fieldKey}Coords`] = null;
    } else {
      patch[`${fieldKey}PageNo`] = pageNo;
      patch[`${fieldKey}Pattern`] = null;
      // Include any box the user dragged via "Adjust Box" (D4: coords win over pattern)
      const draggedCoords = getBoxEdit?.(fieldKey);
      if (draggedCoords) patch[`${fieldKey}Coords`] = draggedCoords;
    }
    return patch;
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const saveData = { ...(liveAdvancedValues ?? {}), ...buildPatch() };
      await mappingApi.updateTemplate(mappingKey, saveData, tenantId);
      setSaved(true);
      onSaved?.();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setError('Rejected: changes would break previously verified data.');
      } else {
        setError(err?.response?.data?.message || 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  const matchBadge = () => {
    if (mode !== 'pattern') return null;
    if (textLoading) return <Spin size="small" />;
    if (!matchResult) return null;
    if (matchResult.error) {
      return <Tag color="red" style={{ fontSize: 11 }}>Invalid regex</Tag>;
    }
    if (matchResult.count === 0) {
      return <Tag color="orange" style={{ fontSize: 11 }}>0 matches</Tag>;
    }
    const uniqueValues = [...new Set(matchResult.values)];
    const allSame = uniqueValues.length <= 1;
    return (
      <Space size={4} wrap>
        <Tag color={allSame ? 'green' : 'orange'} style={{ fontSize: 11 }}>
          {matchResult.count} match{matchResult.count !== 1 ? 'es' : ''}
          {allSame ? '' : ' (inconsistent)'}
        </Tag>
        {uniqueValues.slice(0, 5).map((v, i) => (
          <Tag key={i} color={allSame ? 'green' : 'orange'} style={{ fontSize: 11, fontFamily: 'monospace' }}>
            "{v}"
          </Tag>
        ))}
        {uniqueValues.length > 5 && (
          <Tag style={{ fontSize: 11 }}>+{uniqueValues.length - 5} more</Tag>
        )}
      </Space>
    );
  };

  return (
    <Card
      size="small"
      style={{ background: '#fafafa', border: '1px solid #f0f0f0' }}
      styles={{ body: { padding: 12 } }}
    >
      {/* Extraction source info */}
      <Space size={6} style={{ marginBottom: 10, flexWrap: 'wrap' }}>
        <Text style={{ fontSize: 12, color: '#888' }}>Source:</Text>
        <Tag color={sourceColor} style={{ fontSize: 11 }}>{sourceLabel}</Tag>
        <Text style={{ fontSize: 12, color: '#888' }}>Page:</Text>
        <Tag style={{ fontSize: 11 }}>{Number(currentPageNo) + 1}</Tag>
        {fieldType !== 'STRING' && (
          <Tag style={{ fontSize: 11 }}>{fieldType}</Tag>
        )}
      </Space>

      <Divider style={{ margin: '8px 0' }} />

      {/* Coord vs Pattern toggle — mutually exclusive (D4) */}
      <Space size={6} style={{ marginBottom: 10 }}>
        <Text style={{ fontSize: 12 }}>Extract by:</Text>
        <Button
          size="small"
          type={mode === 'coords' ? 'primary' : 'default'}
          onClick={() => setMode('coords')}
        >
          Coordinates
        </Button>
        <Button
          size="small"
          type={mode === 'pattern' ? 'primary' : 'default'}
          onClick={() => setMode('pattern')}
        >
          Pattern
        </Button>
      </Space>

      {mode === 'coords' && (
        <div style={{ marginBottom: 10 }}>
          <Space size={6} wrap>
            <Text style={{ fontSize: 12 }}>Page No:</Text>
            <Select
              size="small"
              value={pageNo}
              onChange={(v) => {
                setPageNo(v);
                onSetBoxPage?.(fieldKey, Number(v));
              }}
              style={{ width: 80 }}
              options={[0, 1, 2, 3, 4].map((n) => ({
                value: String(n),
                label: `Page ${n + 1}`,
              }))}
            />
          </Space>
          {onActivateBox && (
            <Text style={{ display: 'block', marginTop: 6, fontSize: 11, color: '#888' }}>
              Drag the highlighted box on the PDF to reposition it.
            </Text>
          )}
        </div>
      )}

      {mode === 'pattern' && (
        <div style={{ marginBottom: 10 }}>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              size="small"
              placeholder="(?i)PO[:\s#]+(\S+)"
              value={patternValue}
              onChange={(e) => setPatternValue(e.target.value)}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </Space.Compact>
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text style={{ fontSize: 11, color: '#888' }}>
              Regex on raw PDF text. First capture group = field value.
            </Text>
            {matchBadge()}
          </div>
        </div>
      )}

      {error && (
        <Alert type="error" message={error} showIcon style={{ marginBottom: 8, fontSize: 12 }} />
      )}
      {testResult && (
        <Alert
          type={testResult.matched ? 'success' : 'warning'}
          message={testResult.matched
            ? `Extracted: "${testResult.value}"`
            : 'No value extracted with current settings'}
          showIcon
          style={{ marginBottom: 8, fontSize: 12 }}
        />
      )}
      {saved && (
        <Alert type="success" message="Saved to template." showIcon style={{ marginBottom: 8, fontSize: 12 }} />
      )}

      <Space size={6}>
        <Button
          size="small"
          icon={<ExperimentOutlined />}
          loading={testing}
          onClick={handleTest}
        >
          Test
        </Button>
        <Button
          size="small"
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          onClick={handleSave}
          disabled={mode === 'pattern' && !!matchResult?.error}
        >
          Save to Template
        </Button>
      </Space>
    </Card>
  );
}
